import axios from 'axios';
import { API_BASE_URL, AUTH_BASE_URL, API_ENDPOINTS } from '../config/api';
import { useAuthStore } from '../store/authStore';

// =============================================
// Two axios instances:
// - authApi  -> talks to auth-service (port 3001)
// - api      -> talks to inventory service (port 3017)
// =============================================

const authApi = axios.create({
  baseURL: AUTH_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to both instances
const attachToken = (config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

authApi.interceptors.request.use(attachToken, (e) => Promise.reject(e));
api.interceptors.request.use(attachToken, (e) => Promise.reject(e));

// Handle 401 on both instances
const onError = (error) => {
  if (error.response?.status === 401) {
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
};

authApi.interceptors.response.use((r) => r, onError);
api.interceptors.response.use((r) => r, onError);

// =============================================
// AUTH SERVICE (port 3001)
// =============================================
export const authService = {
  login: async (emailOrPhone, password, dssn) => {
    const response = await authApi.post(API_ENDPOINTS.LOGIN, {
      email_or_phone: emailOrPhone,
      password,
      dssn,
    });
    return response.data;
  },

  verify: async () => {
    const response = await authApi.get(API_ENDPOINTS.VERIFY);
    return response.data;
  },
};

// =============================================
// PRODUCTS (port 3017)
// =============================================
export const productService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.PRODUCTS);
    return response.data;
  },

  getById: async (productId) => {
    const response = await api.get(API_ENDPOINTS.PRODUCT_BY_ID(productId));
    return response.data;
  },

  getByBarcode: async (barcode) => {
    const response = await api.get(API_ENDPOINTS.PRODUCT_BY_BARCODE(barcode));
    return response.data;
  },

  create: async (productData) => {
    const response = await api.post(API_ENDPOINTS.PRODUCTS, productData);
    return response.data;
  },

  update: async (productId, productData) => {
    const response = await api.put(
      API_ENDPOINTS.PRODUCT_BY_ID(productId),
      productData
    );
    return response.data;
  },

  delete: async (productId) => {
    const response = await api.delete(API_ENDPOINTS.PRODUCT_BY_ID(productId));
    return response.data;
  },
};

// =============================================
// STOCK (port 3017)
// =============================================
export const stockService = {
  getLevels: async () => {
    const response = await api.get(API_ENDPOINTS.STOCK_LEVELS);
    return response.data;
  },

  getMovements: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.STOCK_MOVEMENTS, { params });
    return response.data;
  },

  receiveStock: async (data) => {
    const response = await api.post(API_ENDPOINTS.STOCK_IN, data);
    return response.data;
  },

  adjustStock: async (data) => {
    const response = await api.post(API_ENDPOINTS.STOCK_ADJUST, data);
    return response.data;
  },

  getReceipts: async () => {
    const response = await api.get(API_ENDPOINTS.STOCK_RECEIPTS);
    return response.data;
  },
};

// =============================================
// SALES (port 3017)
// =============================================
export const salesService = {
  createSale: async (saleData) => {
    const response = await api.post(API_ENDPOINTS.SALES, saleData);
    return response.data;
  },
};

// =============================================
// REPORTS (port 3017)
// =============================================
export const reportService = {
  getDashboard: async () => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD);
    return response.data;
  },

  getTaxReport: async (periodStart, periodEnd) => {
    const response = await api.get(API_ENDPOINTS.TAX_REPORT, {
      params: {
        period_start: periodStart,
        period_end: periodEnd,
      },
    });
    return response.data;
  },

  getValuation: async () => {
    const response = await api.get(API_ENDPOINTS.VALUATION);
    return response.data;
  },
};

// =============================================
// TAX SERVICE (port 3017) — Wave 1
// =============================================
export const taxService = {
  // Tax summary
  getSummary: async (cycle = 'monthly', referenceDate = null) => {
    const params = { cycle };
    if (referenceDate) params.reference_date = referenceDate;
    const response = await api.get(API_ENDPOINTS.TAX_SUMMARY, { params });
    return response.data;
  },

  // Filings
  createFiling: async ({ cycle, reference_date, notes }) => {
    const response = await api.post(API_ENDPOINTS.TAX_FILINGS, {
      cycle,
      reference_date,
      notes,
    });
    return response.data;
  },

  listFilings: async () => {
    const response = await api.get(API_ENDPOINTS.TAX_FILINGS);
    return response.data;
  },

  getFiling: async (filingId) => {
    const response = await api.get(API_ENDPOINTS.TAX_FILING_BY_ID(filingId));
    return response.data;
  },

  submitFiling: async (filingId) => {
    const response = await api.post(
      API_ENDPOINTS.TAX_FILING_SUBMIT(filingId)
    );
    return response.data;
  },

  // Clearance
  applyClearance: async ({ purpose, period_start, period_end, notes }) => {
    const response = await api.post(API_ENDPOINTS.TAX_CLEARANCE, {
      purpose,
      period_start,
      period_end,
      notes,
    });
    return response.data;
  },

  listClearances: async () => {
    const response = await api.get(API_ENDPOINTS.TAX_CLEARANCE);
    return response.data;
  },
};

// =============================================
// LRA SERVICE (port 3017) — Wave 2
// =============================================
export const lraService = {
  // Get current LRA user (role detection)
  getMe: async () => {
    const response = await api.get(API_ENDPOINTS.LRA_ME);
    return response.data;
  },

  // Commissioner General dashboard
  getIntelligence: async () => {
    const response = await api.get(API_ENDPOINTS.LRA_INTELLIGENCE);
    return response.data;
  },

  // Business directory
  listBusinesses: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.LRA_BUSINESSES, { params });
    return response.data;
  },

  getBusinessProfile: async (dssn) => {
    const response = await api.get(API_ENDPOINTS.LRA_BUSINESS_BY_DSSN(dssn));
    return response.data;
  },

  // Audit signals
  listAuditSignals: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.LRA_AUDIT_SIGNALS, { params });
    return response.data;
  },

  // Run detection engine (Wave 3)
  runDetection: async (dssn = null) => {
    const body = dssn ? { dssn } : {};
    const response = await api.post(
      API_ENDPOINTS.LRA_AUDIT_SIGNAL_DETECT,
      body
    );
    return response.data;
  },

  // Update a signal's status/notes (Wave 3)
  updateAuditSignal: async (id, payload) => {
    const response = await api.patch(
      API_ENDPOINTS.LRA_AUDIT_SIGNAL_BY_ID(id),
      payload
    );
    return response.data;
  },

  // Trends
  getRevenueTrend: async (days = 30) => {
    const response = await api.get(API_ENDPOINTS.LRA_REVENUE_TREND, {
      params: { days },
    });
    return response.data;
  },

  getRegistrationTrend: async (days = 30) => {
    const response = await api.get(API_ENDPOINTS.LRA_REGISTRATION_TREND, {
      params: { days },
    });
    return response.data;
  },

  getDivisions: async () => {
    const response = await api.get(API_ENDPOINTS.LRA_DIVISIONS);
    return response.data;
  },

  getRecentActivity: async (limit = 20) => {
    const response = await api.get(API_ENDPOINTS.LRA_RECENT_ACTIVITY, {
      params: { limit },
    });
    return response.data;
  },

    // Wave 3 — Reports
  getReportsSummary: async (days = 30) => {
    const response = await api.get(API_ENDPOINTS.LRA_REPORTS_SUMMARY, {
      params: { days },
    });
    return response.data;
  },

  getTopTaxpayers: async (days = 30, limit = 20) => {
    const response = await api.get(API_ENDPOINTS.LRA_REPORTS_TOP_TAXPAYERS, {
      params: { days, limit },
    });
    return response.data;
  },
};

// =============================================
// REGISTRATION SERVICE (port 3017) — Wave 3
// =============================================
export const registrationService = {
  // Reference data (counties, business types, etc.)
  getReference: async () => {
    const response = await api.get(API_ENDPOINTS.LRA_REG_REFERENCE);
    return response.data;
  },

  // Preview DSSN + TIN + Business ID (no write)
  preview: async (formData) => {
    const response = await api.post(API_ENDPOINTS.LRA_REG_PREVIEW, formData);
    return response.data;
  },

  // Full registration (writes to users, wallets, tin_linkage, lra_registrations)
  register: async (formData) => {
    const response = await api.post(API_ENDPOINTS.LRA_REG_REGISTER, formData);
    return response.data;
  },

    // Upload a single file (multipart/form-data)
  uploadFile: async (file, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      API_ENDPOINTS.LRA_REG_UPLOAD,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => {
              if (e.total) {
                onProgress(Math.round((e.loaded * 100) / e.total));
              }
            }
          : undefined,
      }
    );
    return response.data;
  },

  // History (list past registrations by this agent)
  listHistory: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.LRA_REG_HISTORY, { params });
    return response.data;
  },

  // Detail of a single registration
  getById: async (registrationId) => {
    const response = await api.get(API_ENDPOINTS.LRA_REG_BY_ID(registrationId));
    return response.data;
  },
};

export default api;
